// Card 28, harbour limits (R2585, R2727; ui-map-interface.md §17.3.28,
// v2-the-deep.md §6.3; mockup round 029 card 28 and round 088 card 02).
//
// A modern chart draws a dashed harbour-limit line round the water of a
// port. Harbour limits are the permanent aura of a hold: inside the
// radius the fleets of the owner and of the owner's squad mates mend
// their shields at the berth rate (R2539). This piece draws the limits of
// ONE hold, the hold the pointer is over or the selection names, as a
// dashed hex outline in the faction ink of the hold, with the name above
// it, in both readings. An installation has no harbour limits, so it gets
// no outline.
//
// **THE LINE OF CARD 30.** The owner ruled the line the same dashed hex
// line that card 30 draws round the apron of a strategic object (R2727):
// the union outline of a set of cells (`outlineStrokes`), in the width and
// the dash of `[chart.piece.restricted]`. The set is every cell whose
// centre lies within the radius of the centre of the hold's cell
// (`cellsWithinPx`), which is the server's own test of the aura
// (`grid.near`: `Dist2 <= r * r` from `CellCenter`).
//
// **HOVER OR SELECTION ONLY.** As card 34, the TOML `show` carries the
// ruling: `"hover"` draws the one hold the pointer or the selection names,
// `"always"` draws every hold.
//
// **THE RADIUS IS THE SERVER'S.** `ChartContext.harbourPx` carries
// `CatalogRules.harbour_limits_px`, and the page writes 0 there while the
// flag `modifiers` is off (§14.1 rule 5: the control reads the flag, the
// piece draws what it is handed). A 0 draws nothing, so a world with the
// flag off shows the chart of today.

import { CHART } from '../../../gen/presentation';
import { SiteKind } from '../../../gen/proto/aquaphobia';
import { chart as S } from '../../../gen/strings';
import { cellsWithinPx } from '../../../render/hex-ring';
import { cellCenter, factionColor } from '../../../world';
import type { ChartContext, ChartSinks, ChartTier, Piece, RebuildReason } from '../context';
import { namedSite } from './beacon-read';
import { dashedStrokes, faceOf, outlineStrokes, showsAlways, type Stroke } from './shared';

/** Does the shipped table draw every hold, or only the named one? */
const HARBOUR_ALWAYS = showsAlways(CHART.pieces.harbour_limits.show);

/** One hold, as the last pack read it. */
interface Harbour {
  x: number;
  /** The top of the outline, where the name stands. */
  top: number;
  faction: number;
  strokes: Stroke[];
}

export class HarbourLimitsPiece implements Piece {
  readonly id = 'harbour_limits' as const;
  /** The screen tier, with cards 30 and 34: a hover repacks this tier. */
  readonly tier: ChartTier = 'screen';
  readonly pack = 'static' as const;
  /** The holds of the season running now: a replay film hides them. */
  readonly film = 'hide' as const;
  readonly rebuildOn: readonly RebuildReason[];

  /** The holds of the last pack. The tests read them. */
  holds: Harbour[] = [];
  /** The radius of the last pack, for `outgrown`. */
  private radius = 0;

  /** `always` is a parameter so the test can state both readings of `show`. */
  constructor(private readonly always: boolean = HARBOUR_ALWAYS) {
    this.rebuildOn = always
      ? ['hello', 'sites', 'reading', 'zoomband', 'font']
      : ['hello', 'sites', 'hover', 'selection', 'reading', 'zoomband', 'font'];
  }

  visible(ctx: ChartContext): boolean {
    return ctx.harbourPx > 0 && (this.always || namedSite(ctx) !== 0);
  }

  rebuild(ctx: ChartContext): void {
    this.radius = ctx.harbourPx;
    this.holds = [];
    if (this.always) {
      for (const s of ctx.state.sites.values()) this.add(s.kind, s.cell, s.faction);
      return;
    }
    const s = ctx.state.sites.get(namedSite(ctx));
    if (s) this.add(s.kind, s.cell, s.faction);
  }

  private add(kind: SiteKind, cell: number, faction: number): void {
    if (kind !== SiteKind.SITE_KIND_PLAYER_BASE || !(this.radius > 0)) return;
    const strokes = outlineStrokes(cellsWithinPx(cell, this.radius));
    if (strokes.length === 0) return;
    const [x] = cellCenter(cell);
    let top = Infinity;
    for (const k of strokes) top = Math.min(top, k.y0, k.y1);
    this.holds.push({ x, top, faction, strokes });
  }

  /** A new radius is an edge no reason names: one compare a frame. */
  outgrown(ctx: ChartContext): boolean {
    return ctx.harbourPx !== this.radius;
  }

  emit(sinks: ChartSinks, ctx: ChartContext): void {
    if (this.holds.length === 0) return;
    const t = ctx.cfg.pieces.harbour_limits;
    const reading = ctx.reading;
    const r = t[reading];
    const face = faceOf(reading);
    const word = reading === 'modern' ? S.harbour_limits_modern : S.harbour_limits_historical;
    for (const h of this.holds) {
      const ink = factionColor(h.faction);
      dashedStrokes(sinks.segments, h.strokes, t.fig.width, ink, t.fig.alpha, r.dash);
      ctx.glyphs.word(
        sinks.quads, word, h.x, h.top - t.fig.label_gap,
        face, t.text.main, ink, r.alpha, { anchor: 'centre' },
      );
    }
  }
}

/** The one instance the registry holds. */
export const harbourLimits = new HarbourLimitsPiece();
